import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@components/inputs/Checkbox/Checkbox";
import { LargeAccordion } from "@components/LargeAccordion/LargeAccordion";
import { ScrollArea } from "@components/ScrollArea/ScrollArea";
import { usePOAStore } from "@store/poaStore/poaStore";
import { type IOverviewJointBillService } from "@services/overview/types";
import { usePoaJbsDataQuery } from "@services/powersOfAttorney/queries";
import { type IPOARecommendationsResponse } from "@services/powersOfAttorney/types";
import { type POAServiceProviderSource } from "../types";
import { PowerOfAttorneyHouseAccordionRow } from "./PowerOfAttorneyHouseAccordionRow";
import { PowerOfAttorneyHouseAccordionSearch } from "./PowerOfAttorneyHouseAccordionSearch";

export interface IPowerOfAttorneyHouseAccordionProps {
  jointBillService: IOverviewJointBillService;
  recommendations?: IPOARecommendationsResponse;
  showExistingConsent?: boolean;
  includeRecommendations?: boolean;
  isFirst?: boolean;
  recommendationSource?: POAServiceProviderSource;
}

export const PowerOfAttorneyHouseAccordion: React.FC<
  IPowerOfAttorneyHouseAccordionProps
> = (props) => {
  const {
    recommendations,
    jointBillService,
    showExistingConsent,
    includeRecommendations,
    isFirst,
    recommendationSource,
  } = props;
  const { t } = useTranslation();

  const { setChecked, setProviderList, checkAll, uncheckAll } = usePOAStore();
  const providerList = usePOAStore(
    (state) => state.providerList[jointBillService.jointBillServiceId],
  );

  const { data: poaJbsData } = usePoaJbsDataQuery(showExistingConsent);

  const poaServiceProviderRequests = poaJbsData?.find(
    (jbs) => jbs.jointBillServiceId === jointBillService.jointBillServiceId,
  )?.serviceProviderRequests;

  useEffect(() => {
    if (recommendations && includeRecommendations && !providerList) {
      setProviderList(
        jointBillService.jointBillServiceId,
        recommendations.powerOfAttorneyProviders.map((provider) => ({
          ...provider,
          id: `${provider.serviceProviderId}_undefined`,
          selected: false,
          serviceProviderSource: recommendationSource,
        })),
      );
    }
  }, [
    recommendations,
    includeRecommendations,
    jointBillService.jointBillServiceId,
    setProviderList,
    providerList,
    recommendationSource,
  ]);

  const isAllChecked = useMemo(
    () => providerList?.every((provider) => provider.selected),
    [providerList],
  );
  const isSomeChecked = useMemo(
    () => providerList?.some((provider) => provider.selected),
    [providerList],
  );

  const rowCount =
    (providerList?.length ?? 0) +
    (showExistingConsent ? (poaServiceProviderRequests?.length ?? 0) : 0);

  return (
    <LargeAccordion
      title={jointBillService.displayName ?? t("common.defaultAddress")}
      isDefaultOpen={isFirst}
    >
      <div className="flex w-full justify-end bg-base-light-grey px-8 py-2">
        {!showExistingConsent ? (
          <div className="flex gap-2">
            <span className="whitespace-nowrap text-xs font-medium text-grey-dark">
              {isAllChecked
                ? t("powerOfAttorney.houseAccordion.deselectAll")
                : t("powerOfAttorney.houseAccordion.selectAll")}
            </span>
            <Checkbox
              checked={isSomeChecked}
              isIndeterminate={!isAllChecked && isSomeChecked}
              onChange={() => {
                if (isAllChecked) {
                  uncheckAll(jointBillService.jointBillServiceId);
                } else {
                  checkAll(jointBillService.jointBillServiceId);
                }
              }}
            />
          </div>
        ) : (
          <div className="size-5" />
        )}
      </div>
      <ScrollArea className="h-96" enabled={rowCount > 6}>
        <div className="flex flex-col divide-y divide-grey-light overflow-hidden px-8">
          {showExistingConsent &&
            poaServiceProviderRequests?.map((provider) => (
              <PowerOfAttorneyHouseAccordionRow
                key={provider.serviceProviderName}
                serviceProviderName={provider.serviceProviderName}
                serviceProviderRequestStateId={
                  provider.serviceProviderRequestStateId
                }
                isLocked
              />
            ))}
          {providerList?.map((provider) => (
            <PowerOfAttorneyHouseAccordionRow
              key={provider.id}
              serviceProviderName={provider.serviceProviderName}
              selected={provider.selected}
              onCheck={() => {
                setChecked(
                  jointBillService.jointBillServiceId,
                  `${provider.serviceProviderId}_${provider.companyCode}`,
                  !provider.selected,
                );
              }}
              serviceProviderSource={provider.serviceProviderSource}
            />
          ))}
        </div>
      </ScrollArea>
      <div className="flex flex-col gap-2 border-t border-grey-light p-6">
        <span className="text-sm font-medium text-grey-dark">
          {t("powerOfAttorney.houseAccordion.cantFindProvider")}
        </span>
        <PowerOfAttorneyHouseAccordionSearch
          jointBillServiceId={jointBillService.jointBillServiceId}
        />
      </div>
    </LargeAccordion>
  );
};
